import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataService } from '../../services/dataService';
import { ContextualChatButton } from '../ContextualChatButton';
import { formatCardDateShort } from '../../utils/dateUtils';
import type { RestDay, MobilityRoutine } from '../../types';
import '../../styles/card-system.css'; // Import card system styles

/**
 * 🛌 REST DAY CARD COMPONENT (Jan 23, 2026)
 * 
 * Structure matches SimpleWorkoutCard EXACTLY:
 * - Single card wrapper (no nested divs)
 * - Teal background applied directly to card
 * - Grey header with date, pill, checkbox, chat
 * - Icon + title section below header
 * - NO bottom content (no time/weather like workout cards)
 */
export const RestDayCardWrapper: React.FC<{
  restDay: RestDay;
  onTap: () => void;
  showTodayBadge?: boolean;
  showDateBadge?: boolean;
}> = ({ restDay, onTap, showTodayBadge = false, showDateBadge = false }) => {
  const navigate = useNavigate();
  const [isCompleted, setIsCompleted] = useState(restDay.status === 'completed');
  const [linkedMobility, setLinkedMobility] = useState<MobilityRoutine[]>([]);
  
  // Load linked mobility routines
  useEffect(() => {
    const loadLinkedMobility = async () => {
      const allData = await DataService.getAllData();
      const dateStr = restDay.scheduledDate ? new Date(restDay.scheduledDate).toISOString().split('T')[0] : '';
      const matchingMobility = allData.mobilityRoutines.filter((m: MobilityRoutine) => {
        const mobilityDateStr = m.scheduledDate ? new Date(m.scheduledDate).toISOString().split('T')[0] : '';
        return mobilityDateStr === dateStr && m.context === 'rest_day';
      });
      setLinkedMobility(matchingMobility);
    };
    
    loadLinkedMobility();
  }, [restDay.id, restDay.scheduledDate]);

  const getRestDayTypeLabel = (type: string): string => {
    switch (type) {
      case 'active-recovery': return 'Active Recovery';
      case 'complete': return 'Full Rest';
      case 'recovery': return 'Recovery';
      case 'complete_rest': return 'Complete Rest';
      default: return 'Rest';
    }
  };
  
  const handleCheckboxClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const previousStatus = isCompleted;
    setIsCompleted(!isCompleted);
    
    try {
      const result = await (isCompleted ? 
        DataService.markRestDayIncomplete(restDay.id) :
        DataService.markRestDayComplete(restDay.id));
      
      if (!result.success) {
        console.error('Failed to update rest day status:', result.message);
        setIsCompleted(previousStatus);
      }
    } catch (error) {
      console.error('Error updating rest day:', error);
      setIsCompleted(previousStatus);
    }
  };

  return (
    <div 
      onClick={onTap}
      className="mypath-card cursor-pointer"
      style={{ 
        background: 'linear-gradient(135deg, rgba(46, 139, 139, 0.15) 0%, rgba(46, 139, 139, 0.08) 100%)', // 🎨 Teal gradient wrapper
        border: 'none',
        borderRadius: '12px',
        padding: '8px', // 🔧 JAN 23, 2026: Increased from 4px to 8px to match workout card visual weight
        overflow: 'visible'
      }}
    >
      {/* INNER WHITE CARD (narrower than wrapper) */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        {/* GREY HEADER: Date | Type Pill | Checkbox | Chat */}
        <div 
          className="flex items-center justify-center px-3 py-2"
          style={{ 
            backgroundColor: 'var(--colorsurface-50)', // Grey header
            borderBottom: '1px solid var(--cedar-limestone)',
            minHeight: '44px' // Ensure consistent header height
          }}
        >
        {/* Left: Date */}
        <div className="flex-shrink-0 flex items-center">
          {restDay.scheduledDate && (showDateBadge || showTodayBadge) ? (
            <div className="mypath-card-label font-medium px-2 py-1 rounded">
              {formatCardDateShort(new Date(restDay.scheduledDate))}
            </div>
          ) : (
            <div style={{ width: '50px' }} />
          )}
        </div>

        {/* Center: Type Pill */}
        <div className="flex-1 flex justify-center items-center">
          <div 
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: 'rgba(46, 139, 139, 0.15)',
              color: '#2E8B8B'
            }}
          >
            {getRestDayTypeLabel(restDay.type)}
          </div>
        </div>

        {/* Right: Checkbox + Chat */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Checkbox */}
          <button
            onClick={handleCheckboxClick}
            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
              isCompleted 
                ? 'bg-green-600' 
                : 'border-2 border-gray-300'
            }`}
            title={isCompleted ? 'Completed' : 'Mark complete'}
          >
            <svg className={`w-3 h-3 ${isCompleted ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>

          {/* Chat */}
          <ContextualChatButton
            context={{
              type: 'rest_day',
              title: restDay.title || getRestDayTypeLabel(restDay.type),
              description: restDay.description || '',
              entity_id: restDay.id,
              metadata: {
                rest_type: restDay.type,
                scheduled_date: restDay.scheduledDate,
                activities: restDay.activities,
                linked_mobility_count: linkedMobility.length
              },
              action: 'discuss'
            }}
            text=""
            size="small"
            variant="clean"
          />
        </div>
      </div>

        {/* BODY: Icon + Title */}
        <div className="px-3 py-3">
        <div className="flex items-center gap-3">
          {/* 🔧 REST text badge (matches weekly plan dots diagram style) */}
          <span
            className="text-[11px] font-bold px-2 py-1 rounded flex-shrink-0"
            style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.12)' }}
          >
            REST
          </span>

          {/* Title: "Rest Day: [Type]" */}
          <div className="flex-1">
            <h3 className="mypath-card-title" style={{ fontSize: '16px', lineHeight: '1.3', margin: 0 }}>
              Rest Day: {restDay.title || getRestDayTypeLabel(restDay.type)}
            </h3>
          </div>
        </div>

          {/* Optional Mobility Indicator */}
          {linkedMobility.length > 0 && (
            <div className="mt-3">
              <div 
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(46, 139, 139, 0.08)',
                  border: '1px solid rgba(46, 139, 139, 0.2)',
                  fontSize: '13px'
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4" style={{ color: '#2E8B8B' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span style={{ fontWeight: 600, color: '#2E8B8B' }}>
                    {linkedMobility.length} Mobility Routine{linkedMobility.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--coloron-surface600)', margin: 0 }}>
                  Tap to view mobility work
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
